import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  api,
  computeBreakdown,
  resolveTiersV2,
  fileUrl,
  publicQuotationUrl,
  API,
} from "@/lib/quotationApi";
import { exportQuotationDualPDFs } from "@/lib/pdfExport";
import { applyBrandingCSS, PUBLIC_DEFAULT_BRANDING } from "@/lib/branding";
import PaymentPlanBlock from "@/components/PaymentPlanBlock";
import PublicHeader from "@/components/public/PublicHeader";
import PublicHero from "@/components/public/PublicHero";
import PublicNotices from "@/components/public/PublicNotices";
import PublicFootnotes from "@/components/public/PublicFootnotes";
import PublicShareBar from "@/components/public/PublicShareBar";
import PublicTierTotals from "@/components/public/PublicTierTotals";
import PublicRooms from "@/components/public/PublicRooms";
import PublicAccessories from "@/components/public/PublicAccessories";
import PublicPaymentInfo from "@/components/public/PublicPaymentInfo";

// Reconstruct a live v2 tier config from a frozen snapshot so we can recompute
// tier totals when the customer toggles items on/off (prices stay frozen; only
// the set of included items changes). Returns null for legacy/empty snapshots.
const reconstructTierConfig = (snap) => {
  if (!snap || !Array.isArray(snap.list) || snap.list.length === 0) return null;
  return {
    modular_multiplier: snap.modular_multiplier ?? 2.5,
    tiers: snap.list.map((t, i) => ({
      id: t.id,
      name: t.name,
      description: t.description,
      color: t.color,
      finish_discount: t.finish_discount,
      hardware_discount: t.hardware_discount,
      modular_multiplier: t.modular_multiplier,
      is_base: t.is_base,
      order: i,
    })),
  };
};

export default function PublicQuotation() {
  const { id } = useParams();
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [excluded, setExcluded] = useState(() => new Set());
  const [branding, setBranding] = useState(PUBLIC_DEFAULT_BRANDING);

  // Snapshot mode (?snap=1) hides the interactive toggle controls so the PDF
  // screenshot renders clean strike-throughs. ?ex=<ids> seeds the excluded set.
  const urlParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );
  const snapshotMode = urlParams.get("snap") === "1";

  const logoUrl = branding.logo_storage_path ? fileUrl(branding.logo_storage_path) : "";
  const primary = branding.primary_color || "#C5A880";
  const brandInitial =
    (branding.firm_name || "L").trim().charAt(0).toUpperCase() || "L";

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/quotations/${id}`);
        const data = r.data;
        setQ(data);

        // Seed the excluded set: honour ?ex= from the URL when present
        // (PDF snapshot render), otherwise default to items the agent has
        // already excluded (is_included === false), which the customer can
        // toggle back on.
        if (urlParams.has("ex")) {
          const raw = urlParams.get("ex") || "";
          setExcluded(new Set(raw.split(",").map((s) => s.trim()).filter(Boolean)));
        } else {
          const def = new Set();
          (data.rooms || []).forEach((room) =>
            (room.measurements || []).forEach((m) => {
              if (m.is_included === false) def.add(m.id);
            }),
          );
          (data.accessories || []).forEach((a) => {
            if (a.is_included === false) def.add(a.id);
          });
          setExcluded(def);
        }

        // Branding now comes embedded (and sanitized) in the quote response.
        if (data?.branding) {
          const merged = { ...PUBLIC_DEFAULT_BRANDING, ...data.branding };
          setBranding(merged);
          applyBrandingCSS(merged.primary_color);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Quotation with is_included flags reflecting the customer's live selection.
  const effectiveQ = useMemo(() => {
    if (!q) return q;
    return {
      ...q,
      rooms: (q.rooms || []).map((room) => ({
        ...room,
        measurements: (room.measurements || []).map((m) => ({
          ...m,
          is_included: !excluded.has(m.id),
        })),
      })),
      accessories: (q.accessories || []).map((a) => ({
        ...a,
        is_included: !excluded.has(a.id),
      })),
    };
  }, [q, excluded]);

  // Live tier list. When nothing is excluded we use the frozen snapshot as-is
  // (zero behaviour change); otherwise we recompute from the reconstructed
  // config with frozen prices so totals reflect the customer's selection.
  const tierList = useMemo(() => {
    if (!q) return [];
    const snap = q.tiers;
    if (excluded.size === 0) {
      return resolveTiersV2(q, snap, null, q.selected_tier_ids).list || [];
    }
    const cfg = reconstructTierConfig(snap);
    if (!cfg) {
      return resolveTiersV2(q, snap, null, q.selected_tier_ids).list || [];
    }
    return resolveTiersV2(effectiveQ, null, cfg, null).list || [];
  }, [q, effectiveQ, excluded]);

  // The saved (agent's) quote totals — the baseline for the "savings" badge.
  const savedTierList = useMemo(() => {
    if (!q) return [];
    return resolveTiersV2(q, q.tiers, null, q.selected_tier_ids).list || [];
  }, [q]);

  // Big tier-total cards only: order highest total → lowest and scale the
  // amount font by rank (biggest / mid / smallest). Stable sort keeps the
  // configured order as a tie-breaker when totals are equal. The per-room
  // grid, finish cascade and payment reference keep the original tierList.
  const sortedTierList = useMemo(
    () =>
      [...tierList].sort(
        (a, z) => (z.breakdown?.grand_total || 0) - (a.breakdown?.grand_total || 0),
      ),
    [tierList],
  );

  const b = useMemo(() => (effectiveQ ? computeBreakdown(effectiveQ) : null), [effectiveQ]);

  const validAccessories = useMemo(
    () => (effectiveQ?.accessories || []).filter((a) => (a.quantity || 0) > 0),
    [effectiveQ],
  );

  const toggleItem = (itemId) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-white/50">
        Loading quotation...
      </div>
    );

  if (error || !q)
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <div className="font-display text-2xl mb-2">Quotation not found</div>
          <p className="text-white/50 text-sm">This link may have expired or been removed.</p>
          <Link to="/" className="text-[#C5A880] text-sm underline mt-4 inline-block">
            Go home
          </Link>
        </div>
      </div>
    );

  const hasExclusions = excluded.size > 0;
  const canRecompute = Boolean(reconstructTierConfig(q.tiers));

  // Savings vs the saved (full) quote, measured on the current top tier so the
  // headline number stays meaningful. Positive = customer removed items.
  const heroTier = sortedTierList[0];
  const savedHeroTotal =
    savedTierList.find((t) => t.id === heroTier?.id)?.breakdown?.grand_total || 0;
  const currentHeroTotal = heroTier?.breakdown?.grand_total || 0;
  const savingsNet = Math.round(savedHeroTotal - currentHeroTotal);
  const showSavings = savedHeroTotal > 0 && savingsNet !== 0;

  const onDownloadPDF = async () => {
    if (downloading) return;
    setDownloading(true);
    toast.message("Preparing PDFs…", {
      description: "Color + B&W copies will start downloading shortly.",
    });
    try {
      // B&W vector PDF renders from data: pass the effective quote so excluded
      // items show struck-through and totals reflect the selection.
      const cfg = reconstructTierConfig(q.tiers);
      const pdfData =
        hasExclusions && cfg ? { ...effectiveQ, tiers: null } : effectiveQ;
      const tierSettings =
        hasExclusions && cfg
          ? { tierConfigV2: cfg, selectedTierIds: null }
          : { tiersSnapshot: q.tiers, selectedTierIds: q.selected_tier_ids };

      // Color snapshot renders the public page in an iframe — pass the current
      // selection so the screenshot matches (snap=1 hides the toggle controls).
      const exList = Array.from(excluded);
      const shareUrl = `${publicQuotationUrl(q.id)}?snap=1&ex=${encodeURIComponent(exList.join(","))}`;

      await exportQuotationDualPDFs(pdfData, q.id, shareUrl, tierSettings, {
        branding,
        logoUrl,
        apiBase: API,
      });
      toast.success("Both PDFs downloaded");
    } catch {
      toast.error("PDF generation failed");
    } finally {
      setDownloading(false);
    }
  };

  const onShare = async () => {
    const url = publicQuotationUrl(q.id);
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${branding.firm_name} Quotation — ${q.customer.name}`,
          url,
        });
      } catch (e) {
        // User-cancelled share is normal; only log unexpected failures.
        if (e && e.name !== "AbortError") {
          console.warn("navigator.share failed:", e);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      } catch {
        toast.error("Share unavailable");
      }
    }
  };

  return (
    <div className="min-h-screen pb-20">
      <PublicHeader
        branding={branding}
        logoUrl={logoUrl}
        primary={primary}
        brandInitial={brandInitial}
        refId={q.id.slice(0, 8)}
      />

      <main className="max-w-2xl mx-auto px-4 py-6">
        <PublicHero customer={q.customer} createdAt={q.created_at} />

        <PublicNotices
          snapshotMode={snapshotMode}
          canRecompute={canRecompute}
          hasExclusions={hasExclusions}
          showSavings={showSavings}
          savingsNet={savingsNet}
        />

        <PublicTierTotals
          sortedTierList={sortedTierList}
          tierList={tierList}
          effectiveQ={effectiveQ}
          excluded={excluded}
          breakdown={b}
        />

        <PublicRooms
          effectiveQ={effectiveQ}
          tierList={tierList}
          sortedTierList={sortedTierList}
          excluded={excluded}
          snapshotMode={snapshotMode}
          toggleItem={toggleItem}
          primary={primary}
        />

        <PublicAccessories
          validAccessories={validAccessories}
          excluded={excluded}
          snapshotMode={snapshotMode}
          toggleItem={toggleItem}
          primary={primary}
        />

        <PublicPaymentInfo branding={branding} primary={primary} />

        {/* Payment plan */}
        <PaymentPlanBlock
          plan={q.payment_plan}
          testId="public-payment-plan"
          primary={primary}
        />

        <PublicFootnotes q={q} branding={branding} />
      </main>

      {/* Sticky share bar (hidden in PDF snapshot mode) */}
      {!snapshotMode && (
        <PublicShareBar
          onShare={onShare}
          onDownloadPDF={onDownloadPDF}
          downloading={downloading}
        />
      )}
    </div>
  );
}
